/* React Higher Order Component which fetches organization activity meta tag data for organization where id = props.orgId
    Child (WrappedComponent) will only render once meta tag data is successfully fetched.
    child will receive props: {
        orgId:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        orgActMetaTags: PropTypes.object,
        actMetaTagCategories: PropTypes.object,
        actMetaTypes: PropTypes.array,
    }

*/

import React from 'react';
import PropTypes from 'prop-types';
const has = require('lodash/has');
const uuid = require('uuid');

const Globals = require('../../../../app/globals');
const FeatureUtil = require('../../../../app/shared/FeatureUtil');
const PermissionsUtil = require('../../../../app/shared/PermissionsUtil');
const ActivityActions = require('../../actions/ActivityActions');
const ActivityStore = require('../../stores/activity_store_reflux');
const ChannelStore = require('../../stores/channel_store_reflux');
import LoadingMainContent from '../loading/LoadingMainContent';
import ErrorBox from '../common/ErrorBox';
const ReactUtil = require('../../util/ReactUtil');

const propTypes = {
    orgId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    renderBeforeLodingTags: PropTypes.bool,
    forceRefresh: PropTypes.bool,
};

const defaultProps = {
    renderBeforeLodingTags: false,
    forceRefresh: false,
};

export default function withOrganizationActivityMetaTags(WrappedComponent) {
    class WithOrganizationActivityMetaTags extends React.Component {
        constructor(props) {
            super(props);

            const actStoreState = ActivityStore.getState();
            this.state = {
                orgActMetaTags: actStoreState.activityMetaTags,
                actMetaTagCategories: actStoreState.activityMetaTagCategories,
                orgActMetaTypes: actStoreState.activityMetaTypes,
                getTagsActionId: null,
                getTypesActionId: null,
                fatalError: null
            };

            this.getOrgData = this.getOrgData.bind(this);
            this.isDataLoaded = this.isDataLoaded.bind(this);
            this.unsubscribes = [
                ActivityStore.listen(this.onActivityStoreChange.bind(this)),
                ChannelStore.listen(this.onChannelStoreChange.bind(this))
            ];
        }

        componentDidMount() {
            this.getOrgData(this.props.orgId, this.props.forceRefresh);
        }

        componentDidUpdate(prevProps) {
            if (prevProps.orgId !== this.props.orgId) {
                this.getOrgData(this.props.orgId, this.props.forceRefresh);
            }
        }

        componentWillUnmount() {
            this.unsubscribes.forEach(u => u());
        }

        isDataLoaded(orgId) {
            if (
                !!this.state.actMetaTagCategories
                && !!this.state.orgActMetaTags[orgId]
                && !!this.state.orgActMetaTypes[orgId]
            ) {
                // ensure sample of both orgActMetaTags & orgActMetaTypes have usage_count prop. otherwise load org-level data which includes this prop.
                let orgActMetaTagsFormatCorrect;
                if (Object.keys(this.state.orgActMetaTags[orgId]).length > 0) {
                    const tagsForFirstCat = this.state.orgActMetaTags[orgId][Object.keys(this.state.orgActMetaTags[orgId])[0]];
                    const firstFoundTag = tagsForFirstCat[Object.keys(tagsForFirstCat)[0]];
                    if (has(firstFoundTag, 'usage_count')) {
                        orgActMetaTagsFormatCorrect = true;
                    }
                    else {
                        orgActMetaTagsFormatCorrect = false;
                    }
                }
                else {
                    orgActMetaTagsFormatCorrect = true; // empty
                }
                
                let orgActMetaTypesFormatCorrect;
                if (this.state.orgActMetaTypes[orgId].length > 0) {
                    if (has(this.state.orgActMetaTypes[orgId][0], 'usage_count')) {
                        orgActMetaTypesFormatCorrect = true;
                    }
                    else {
                        orgActMetaTypesFormatCorrect = false;
                    }
                }
                else {
                    orgActMetaTypesFormatCorrect = true; // empty
                }


                return orgActMetaTagsFormatCorrect && orgActMetaTypesFormatCorrect;
            }

            return false;
        }

        getOrgData(orgId, forceRefresh=false) {
            const channelStoreState = ChannelStore.getStateChannel(orgId);
            if (
                channelStoreState.initialized
                && FeatureUtil.hasActivityMetaTags(channelStoreState.channel.channel_permissions)
                && PermissionsUtil.canManageActMetaTags(channelStoreState.channel.user_permissions)
            ) {
                if (
                    !forceRefresh
                    && this.isDataLoaded(orgId)
                ) { // if tags + categories + types are already populated do not refetch, unless forceRefresh is true
                    return;
                }

                const getTagsActionId = uuid.v4();
                const getTypesActionId = uuid.v4();
                this.setState({ getTagsActionId: getTagsActionId, getTypesActionId: getTypesActionId }, () => {
                    ActivityActions.getActMetaTagsForOrg(getTagsActionId, { orgId: orgId });
                    ActivityActions.getActMetaTypesForOrg(getTypesActionId, { orgId: orgId });
                });
            }
        }

        onActivityStoreChange(newState) {
            let additionalState = {};
            if (newState.action.id === this.state.getTagsActionId) {
                additionalState = { ...additionalState, getTagsActionId: null };

                if (!!newState.action.error) {
                    additionalState = { ...additionalState, fatalError: newState.action.error };
                }
            }

            if (newState.action.id === this.state.getTypesActionId) {
                additionalState = { ...additionalState, getTypesActionId: null };

                if (!!newState.action.error) {
                    additionalState = { ...additionalState, fatalError: newState.action.error };
                }
            }

            const actStoreState = ActivityStore.getState();
            this.setState({
                orgActMetaTags: actStoreState.activityMetaTags,
                actMetaTagCategories: actStoreState.activityMetaTagCategories,
                orgActMetaTypes: actStoreState.activityMetaTypes,
                ...additionalState
            });
        }

        onChannelStoreChange(newState) {
            this.getOrgData(this.props.orgId);
        }

        render() {
            if (this.state.fatalError) {
                return <ErrorBox error={this.state.fatalError} allowStackTrace={true} />;
            }

            if (
                !this.isDataLoaded(this.props.orgId)
                && !this.props.renderBeforeLodingTags
            ) {
                return (
                    <div className="assignment-instructor">
                        <LoadingMainContent />
                    </div>
                );
            }

            return (
                <WrappedComponent
                    {...this.props}
                    orgActMetaTags={this.state.orgActMetaTags[this.props.orgId]}
                    actMetaTagCategories={this.state.actMetaTagCategories}
                    orgActMetaTypes={this.state.orgActMetaTypes[this.props.orgId]}
                />
            );
        }
    }

    WithOrganizationActivityMetaTags.propTypes = propTypes;
    WithOrganizationActivityMetaTags.defaultProps = defaultProps;
    WithOrganizationActivityMetaTags.displayName = `WithOrganizationActivityMetaTags(${ReactUtil.getComponentDisplayName(WrappedComponent)})`;
    return WithOrganizationActivityMetaTags;
}